<template>
  <main class="animal-page">
    <!-- HERO / HEADER -->
    <header class="animal-hero">
      <div class="animal-hero-inner">
        <div class="animal-titleRow">
          <div class="animal-titleCol">
            <h2 class="font-weight-bold mb-4 centers">{{ animal.name || "—" }}</h2>

            <div class="animal-chips">
              <span class="chip chip-solid">{{ labelType(animal.type) }}</span>
              <span v-if="animal.naissance" class="chip">🕒 {{ calculateAge(animal.naissance) }}</span>
              <span v-if="animal.sexe" class="chip">{{ sexLabel(animal.sexe) }}</span>
              <span v-if="animal.ville" class="chip">📍 {{ animal.ville }}</span>
              <span v-if="animal.race" class="chip">{{ animal.race }}</span>
              <span v-if="animal.couleur" class="chip">{{ animal.couleur }}</span>
            </div>

            <p v-if="animal.huge_information" class="animal-highlight">
              <span class="animal-highlight-badge">Important</span>
              <span class="animal-highlight-text">{{ animal.huge_information }}</span>
            </p>
          </div>
        </div>
      </div>
    </header>

    <!-- CONTENT -->
    <section class="animal-wrap">
      <div class="animal-grid">
        <!-- LEFT (sticky info) -->
        <aside class="animal-left">
          <div class="card card-sticky">
            <div class="card-head">
              <h2 class="card-title">Infos</h2>
              <div class="card-sub">Tout ce qu’il faut savoir</div>
            </div>

            <div class="kv">
              <div class="kv-row" v-if="animal.naissance">
                <div class="k">Date naissance</div>
                <div class="v">{{ formaterDate(animal.naissance) }}</div>
              </div>

              <div class="kv-row" v-if="animal.arrive">
                <div class="k">Date d’arrivée</div>
                <div class="v">{{ formaterDate(animal.arrive) }}</div>
              </div>

              <div class="kv-row" v-if="animal.sexe">
                <div class="k">Sexe</div>
                <div class="v">{{ sexLabel(animal.sexe) }}</div>
              </div>

              <div class="kv-row" v-if="animal.caractere">
                <div class="k">Caractère</div>
                <div class="v">{{ animal.caractere }}</div>
              </div>

              <div class="kv-row" v-if="animal.ville">
                <div class="k">Ville</div>
                <div class="v">{{ animal.ville }}</div>
              </div>

              <div class="kv-row" v-if="animal.race">
                <div class="k">Race</div>
                <div class="v">{{ animal.race }}</div>
              </div>

              <div class="kv-row" v-if="animal.couleur">
                <div class="k">Couleur</div>
                <div class="v">{{ animal.couleur }}</div>
              </div>

              <div class="kv-row" v-if="animal.sterilise">
                <div class="k">Stérilisé</div>
                <div class="v">{{ animal.sterilise === 'oui' ? 'Oui' : animal.sterilise }}</div>
              </div>

              <div class="kv-row" v-if="animal.identifie">
                <div class="k">Identifié</div>
                <div class="v">{{ animal.identifie === 'oui' ? 'Oui' : animal.identifie }}</div>
              </div>

              <div class="kv-row" v-if="animal.vaccin">
                <div class="k">Vacciné</div>
                <div class="v">{{ animal.vaccin === 'oui' ? 'Oui' : animal.vaccin }}</div>
              </div>

              <div class="kv-row" v-if="animal.specific_needs">
                <div class="k">Besoins spécifiques</div>
                <div class="v">{{ animal.specific_needs }}</div>
              </div>

              <div class="kv-row" v-if="animal.adopte">
                <div class="k">Adopté</div>
                <div class="v">{{ animal.adopte === 'oui' ? 'Oui' : animal.adopte }}</div>
              </div>

              <div class="kv-row" v-if="animal.date_adoption">
                <div class="k">Date d'adoption</div>
                <div class="v">{{ formaterDate(animal.date_adoption) }}</div>
              </div>

              <div class="kv-row" v-if="animal.parrain">
                <div class="k">Parrain</div>
                <div class="v">{{ animal.parrain }}</div>
              </div>

              <div class="kv-row" v-if="animal.fourriere">
                <div class="k">Fourrière</div>
                <div class="v">{{ animal.fourriere ? 'Oui' : 'Non' }}</div>
              </div>

              <div class="kv-row" v-if="animal.mort">
                <div class="k">Décédé</div>
                <div class="v">{{ animal.mort ? 'Oui' : 'Non' }}</div>
              </div>

              <div class="kv-row" v-if="animal.sos">
                <div class="k">SOS</div>
                <div class="v">{{ animal.sos }}</div>
              </div>

              <div class="kv-row" v-if="animal.fiv">
                <div class="k">FIV</div>
                <div class="v">{{ animal.fiv }}</div>
              </div>
            </div>

            <div class="divider"></div>

            <div class="card-head">
              <h2 class="card-title">Compatibilités</h2>
              <div class="card-sub">Ententes</div>
            </div>

            <div class="compat">
                  <div class="compat-item" v-if="animal.entente_enfant">
                  <div class="compat-k">Enfants</div>
                  <div class="compat-v">{{ compatLabel(animal.entente_enfant) }}</div>
                  </div>

                  <div class="compat-item" v-if="animal.entente_chien">
                  <div class="compat-k">Chiens</div>
                  <div class="compat-v">{{ compatLabel(animal.entente_chien) }}</div>
                  </div>

                  <div class="compat-item" v-if="animal.entente_chat">
                  <div class="compat-k">Chats</div>
                  <div class="compat-v">{{ compatLabel(animal.entente_chat) }}</div>
                  </div>
              <div
                v-if="!animal.entente_enfant && !animal.entente_chien && !animal.entente_chat"
                class="empty-inline"
              >
                Aucune info de compatibilité pour le moment.
              </div>
            </div>

            <div class="ctaBox">
              <router-link
                class="ctaLink"
                :to="{ path: '/contact', query: { action: 'adoption', animal: animal.name || '' } }"
              >
                <div class="ctaTitle">Intéressé(e) ?</div>
                <p class="ctaText">
                  Contactez nous pour organiser une rencontre avec {{ animal.name || 'cet animal' }}.
                </p>
              </router-link>
            </div>

            <router-link v-if="nextAnimal" class="btn btn-ghost w" :to="`/animaux/${nextAnimal.id}`">
              Prochain animal
            </router-link>

            <br />
          </div>
        </aside>

        <!-- RIGHT -->
        <div class="animal-right">
          <!-- Description -->
          <section class="card">
            <div class="card-head">
              <h2 class="card-title">À propos</h2>
              <div class="card-sub">Description</div>
            </div>

            <div class="wysiwyg" v-html="descriAnimal || '<p>Aucune description pour le moment.</p>'"></div>
          </section>

          <!-- Photos -->
          <section class="card" id="photos">
            <div class="card-head">
              <h2 class="card-title">Photos</h2>
              <div class="card-sub">{{ photos.length }} photo(s)</div>
            </div>

            <div v-if="photos.length" class="masonry">
              <button
                v-for="(p, i) in photos"
                :key="i"
                class="tile"
                type="button"
                @click="openLightbox(i)"
                :title="`Agrandir (${i + 1}/${photos.length})`"
              >
                <img :src="p" :alt="`Photo ${animal.name || ''} ${i + 1}`" loading="lazy" @error="onImgError" />
                <div class="tileOverlay">
                  <span class="tileHint">Agrandir</span>
                </div>
              </button>
            </div>

            <div v-else class="empty-inline">Aucune photo pour le moment.</div>
          </section>

          <!-- Videos -->
          <section v-if="videos.length" class="card" id="videos">
            <div class="card-head">
              <h2 class="card-title">Vidéos</h2>
              <div class="card-sub">{{ videos.length }} vidéo(s)</div>
            </div>

            <div class="masonry">
              <div v-for="(v, i) in videos" :key="i" class="tile">
                <video controls :src="v" :title="`Vidéo ${animal.name || ''} ${i + 1}`" loading="lazy"></video>
              </div>
            </div>
          </section>
        </div>
      </div>
    </section>

    <!-- LIGHTBOX -->
    <div v-if="lightboxOpen" class="lb" @click.self="closeLightbox">
      <div class="lb-inner">
        <button class="lb-btn lb-close" type="button" @click="closeLightbox" aria-label="Fermer">✕</button>

        <button v-if="photos.length > 1" class="lb-btn lb-prev" type="button" @click="prevPhoto" aria-label="Précédent">
          ‹
        </button>

        <img class="lb-img" :src="photos[lightboxIndex]" :alt="animal.name" @error="onImgError" />

        <button v-if="photos.length > 1" class="lb-btn lb-next" type="button" @click="nextPhoto" aria-label="Suivant">
          ›
        </button>

        <div class="lb-meta">
          <div class="lb-name">{{ animal.name }}</div>
          <div class="lb-count">{{ lightboxIndex + 1 }} / {{ photos.length }}</div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import convertAge from "../components/getAge.js";

export default {
  data() {
    return {
      animal: {},
      animaux: [],
      formatdatearrive: null,
      formatdatenaissance: null,

      lightboxOpen: false,
      lightboxIndex: 0
    };
  },

watch: {
  "$route.params.id": {
    immediate: true,
    async handler(newId) {
      this.loadAnimal(newId);

      await this.$nextTick();
      window.scrollTo({ top: -1000, left: 0, behavior: "auto" });

      setTimeout(() => {
        window.scrollTo({ top: -1000, left: 0, behavior: "auto" });
      }, 50);
    }
  }
},

  computed: {
    descriAnimal() {
      if (this.animal.description) {
        return this.animal.description.replace(/\r\n/g, "<br>");
      }
      return "";
    },

    photos() {
      const base = "https://admin.asso-refuge.fr/images/";
      const a = this.animal || {};
      return [a.img1, a.img2, a.img3, a.img4, a.img5].filter(Boolean).map(f => `${base}${f}`);
    },

    videos() {
      const a = this.animal || {};
      return [a.video1, a.video2].filter(Boolean);
    },

    nextAnimal() {
      if (!this.animaux.length || !this.animal?.type) return null;

      const sameType = this.animaux.filter(a => a.type === this.animal.type);
      if (sameType.length <= 1) return null;

      const currentIndex = sameType.findIndex(a => a.id === this.animal.id);
      const nextIndex = (currentIndex + 1) % sameType.length;
      return sameType[nextIndex];
    }
  },

  methods: {
    loadAnimal(id) {
      const idSite = this.$idSite;

      fetch(`https://admin.asso-refuge.fr/getAnimalDetails/${idSite}/${id}`)
        .then(res => res.json())
        .then(data => {
          this.animal = Array.isArray(data) ? (data[0] || {}) : (data?.data || data || {});
          this.formatdatearrive = data.formatdatearrive;
          this.formatdatenaissance = data.formatdatenaissance;

          this.lightboxOpen = false;
          this.lightboxIndex = 0;
        })
        .catch(err => console.error("Erreur fetch animal:", err));

      if (!this.animaux.length) {
        fetch(`https://admin.asso-refuge.fr/getAnimal/${idSite}`)
          .then(res => res.json())
          .then(data => {
            this.animaux = Array.isArray(data) ? data : (data?.data ?? []);
          })
          .catch(err => console.error("Erreur fetch animaux:", err));
      }
    },

    calculateAge(dateOfBirth) {
      return convertAge(dateOfBirth);
    },

    formaterDate(dateString) {
      if (!dateString) return "Inconnu";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Inconnu";
      return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
    },

    labelType(type) {
      if (!type) return "Animal";
      const t = String(type).toLowerCase();
      if (t === "chien") return "Chien";
      if (t === "chat") return "Chat";
      return t.charAt(0).toUpperCase() + t.slice(1);
    },

    compatLabel(value) {
      const v = String(value ?? "").trim().toLowerCase();

      if (v === "inconnu") {
         const s = String(this.animal?.sexe ?? "").toLowerCase();
         const isFemale = s === "female" || s === "femelle" || s === "f";
         return isFemale ? "Inconnue" : "Inconnu";
      }

      // optionnel : normaliser "bonne/mauvaise"
      if (v === "bonne") return "Bonne";
      if (v === "mauvaise") return "Mauvaise";
      if (v === "moyenne") return "Moyenne";


      // sinon on renvoie tel quel
      return value;
      },

    sexLabel(sexe) {
      const s = String(sexe ?? "").toLowerCase();
      if (s === "female" || s === "femelle" || s === "f") return "Femelle";
      if (s === "male" || s === "m") return "Mâle";
      return sexe || "—";
    },

    openLightbox(i) {
      this.lightboxIndex = i;
      this.lightboxOpen = true;
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
    },

    closeLightbox() {
      this.lightboxOpen = false;
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    },

    nextPhoto() {
      if (!this.photos.length) return;
      this.lightboxIndex = (this.lightboxIndex + 1) % this.photos.length;
    },

    prevPhoto() {
      if (!this.photos.length) return;
      this.lightboxIndex = (this.lightboxIndex - 1 + this.photos.length) % this.photos.length;
    },

    onImgError(e) {
      e.target.src =
        "data:image/svg+xml;charset=UTF-8," +
        encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="900" height="650">
            <defs>
              <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stop-color="#f3f4f6"/>
                <stop offset="1" stop-color="#e5e7eb"/>
              </linearGradient>
            </defs>
            <rect width="100%" height="100%" fill="url(#g)"/>
            <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
              fill="#111827" font-family="Arial" font-size="24">Photo indisponible</text>
          </svg>
        `);
    }
  }
};
</script>

<style scoped src="../assets/css/animauxdetails.css"></style>
